import { action, computed, observable } from 'mobx'
import momentTz from 'moment-timezone'
import {
  CancellationReason,
  ConsultationFormStatus,
  ExternalBookingPaymentStatus,
  ExternalBookingStatus,
  PaymentMethodOptionId,
  PaymentTypes,
  ScheduleEntryType,
  UpfrontPaymentOption,
} from '../../../graphQL/generated/enums'
import ModelStore from '../../../shared-lib/stores/ModelStore'
import { colorOptions } from '../../../shared-lib/timeHelpers'
import { dateInTimezone, dateToDay, timeBrackets } from '../../../shared-lib/utils'
import { $ExternalClientAddress } from '../../../shared-solo/stores/ExternalClientStore/ExternalClientModel'
import { calculateTotalForPercentage } from '../../../shared-lib/helpers/externalBooking'
import { getServiceFee, normaliseServiceFeePercentage } from '../../../shared-lib/lib/serviceFeeHelper'
import { $RootStore } from '../../../src/stores/RootStore'
import { SERVICE_FEE_ITEM_NAME, TRAVEL_FEE_CATEGORY } from 'shared-lib/commonStrings'
import { requestErrorCode } from '../../../shared-lib/communication/BaseErrorHandler'
import { BOOKING_REMINDER_NOT_SENT } from '../../../shared-lib/communication/ErrorHelper'
import { isLegacyDepositBooking } from '../../lib/utils'
import { bookingAcceptsPayments } from '../../lib/bookingStatusBadges'

const LEGACY_SERVICE_FEE_ITEM_NAME = 'Legacy Service Fee' // marker `name` for a normalised legacy deposit-flow fee, pending the pro updating it to the current rate
const MAX_CHARGE_MULTIPLE_OF_ORIGINAL_PRICE = 2

export interface $ExternalBookingMutable extends $ExternalBooking {
  payments: Omit<StripePaymentDetails, 'paymentIntent'>[]
  activityLog: $ExternalBookingActivityItem[]
}

export interface $ExternalBookingActivityItem {
  title: string
  date: Date
  properties?: {
    amount?: number
    description?: string
    paymentType?: string
  }
}

export interface PromocodeDiscount {
  code: string
  amount: number
  type: string
}

export interface $ExternalBooking {
  _id: string | undefined
  professionalId: string
  date: string
  color: string
  title: string
  notes: string
  totalFee: number | undefined
  feeAtBookingTime?: number | undefined
  // Service-fee % applied at booking creation; locks the booking's fee % against later
  // changes to the pro's soloServiceFee setting.
  serviceFeePercentage?: number
  totalDuration: number
  address: $ExternalClientAddress | undefined
  timezone: string
  externalClientId?: string
  externalClient?: ExternalClient
  readonly payments: readonly (Omit<StripePaymentDetails, 'paymentIntent'>)[]
  items: Array<$ExternalBookingItem>
  bufferTime: number
  type: 'external' | 'event' | 'reschedule'
  status: ExternalBookingStatus
  source: ExternalBookingSource
  readonly activityLog: readonly $ExternalBookingActivityItem[]
  depositPercentage: number
  useFixedAmount?: boolean
  paymentMethod: PaymentMethodOptionId
  cancellationText: string
  cancellationReason?: CancellationReason
  externalReviewId?: string
  externalReview?: ExternalReview
  consultationFormStatus: ConsultationFormStatus
  consultationFormSectionIds: string[]
  emailUpdatesEnabled?: boolean
  businessName?: string
  paymentStatus: ExternalBookingPaymentStatus
  platformCommission: number
  promocodeId: string
  promocodeDiscount: PromocodeDiscount
  repeats?: string[]
  termsAndConditionsAccepted?: boolean
  calendarId?: string
  isProfessionalAddress?: boolean
  allocatedProfessionalId?: string
  depositRequired?: UpfrontPaymentOption
  newCustomer?: boolean
  updatedAt?: Date
  createdAt?: Date
  rescheduleBookingId?: string
  originalBookingId?: string
  rescheduleType?: RescheduleType
  rescheduleReason?: string
  tags?: Record<string, string>
}

export interface $ExternalBookingItem {
  _id: string
  treatmentId?: string
  name: string
  fee: number
  duration: number
  category: string
  schedule?: Array<{ duration: number; type: ScheduleEntryType; description?: string }>
}

const externalBookingDefaults: Omit<$ExternalBooking, 'timezone'> = {
  _id: undefined,
  professionalId: '',
  date: new Date().toISOString(),
  title: '',
  notes: '',
  color: colorOptions[0],
  totalDuration: 0,
  totalFee: undefined,
  address: undefined,
  items: [],
  externalClientId: undefined,
  externalClient: undefined,
  payments: [],
  bufferTime: 0,
  type: 'external',
  status: 'ACCEPTED' as ExternalBookingStatus,
  source: 'PRO_APP',
  activityLog: [],
  depositPercentage: 0,
  useFixedAmount: false,
  paymentMethod: PaymentMethodOptionId.ManualCollection,
  cancellationText: '',
  consultationFormStatus: ConsultationFormStatus.NotRequired,
  consultationFormSectionIds: [],
  emailUpdatesEnabled: true,
  businessName: undefined,
  paymentStatus: ExternalBookingPaymentStatus.NoDepositNotPaid,
  platformCommission: 0,
  promocodeId: '',
  promocodeDiscount: {
    code: '',
    amount: 0,
    type: '',
  },
  newCustomer: false,
  allocatedProfessionalId: undefined,
}

export type $ExternalBookingModel = ExternalBookingModel

export default class ExternalBookingModel extends ModelStore {
  @observable accessor data: $ExternalBooking

  fetchMethodName = 'externalBooking'
  saveMethodName = 'createOrUpdateExternalBooking'
  removeMethodName = 'deleteExternalBooking'

  // Transient. Set by a caller immediately before `save_api()` to request a
  // $unset of these fields server-side (see createOrUpdateExternalBooking's
  // unsetFields arg). Cleared by the caller after save_api returns.
  pendingUnsetFields?: string[]

  constructor(data: Partial<$ExternalBooking>, rootStore: $RootStore) {
    super(data, rootStore)

    // Base ModelStore.merge is a bound class-field arrow, so it can't be overridden via `super` or
    // a prototype method. Wrap it here instead: every fetch response flows through merge, so this is
    // the single chokepoint where we normalise a legacy deposit-flow booking into the canonical
    // shape (see normaliseLegacyServiceFee).
    const baseMerge = this.merge
    this.merge = action((o: any) => {
      baseMerge(o)
      this.normaliseLegacyServiceFee()
    })

    this.data = {
      ...externalBookingDefaults,
      timezone: rootStore.userStore.timezone,
    }
    this.merge({
      ...data,
      tags: {
        ...(data.tags || {}),
        country: rootStore.userStore.data.countryISOCode,
      },
    })
  }

  @action.bound mergeFromServer = (booking: any) => {
    this.merge(booking)
    this.listStore.mergeData([booking])
    this.rootStore.agendaStore.resetCache([dateToDay(this.dateAsDate)])
  }

  refundStripePayment_api = async (sessionId: string, amountToRefund: number) => {
    if (!this.data._id) return

    const { communication: { requester } } = this.rootStore
    this.setLoading(true)

    try {
      const response = await requester.refundStripePayment({
        data: {
          externalBookingId: this.data._id,
          _id: sessionId,
          amountToRefund,
        },
      })

      this.setLoading(false)

      if (!response.refundStripePayment) return

      this.mergeFromServer(response.refundStripePayment)

      this.setLoading(false)
    } catch (e) {
      this.setLoading(false)
    }
  }

  applyGiftCard_api = async (giftCardId: string) => {
    if (!this.data._id || !giftCardId) return

    const { communication: { requester } } = this.rootStore
    this.setLoading(true)

    try {
      const response = await requester.applyExternalGiftCard({
        externalBookingId: this.data._id,
        _id: giftCardId,
      })

      this.setLoading(false)

      if (!response.applyExternalGiftCard) return

      this.mergeFromServer(response.applyExternalGiftCard)

      this.setLoading(false)
    } catch (e) {
      this.setLoading(false)
    }
  }

  refundGiftCardPayment_api = async (giftCardId: string, amount: number) => {
    if (!this.data._id) return

    const { communication: { requester } } = this.rootStore
    this.setLoading(true)

    try {
      const response = await requester.refundExternalGiftCardPayment({
        externalBookingId: this.data._id,
        _id: giftCardId,
        amount,
      })

      this.setLoading(false)

      if (!response.refundExternalGiftCardPayment) return

      this.mergeFromServer(response.refundExternalGiftCardPayment)

      this.setLoading(false)
    } catch (e) {
      this.setLoading(false)
    }
  }

  acceptExternalBooking_api = async () => {
    const { communication: { requester }, externalPromocodeStore } = this.rootStore
    const { _id } = this.data

    if (!_id) return
    this.setLoading(true)

    try {
      if (this.hasPromocode) {
        const promocodeData = {
          code: this.data.promocodeDiscount?.code || '',
          clientId: this.data.externalClientId || '',
          professionalId: this.ownerId,
          bookingData: { treatmentIds: this.billableItems.map(item => item.treatmentId || '') || [], bookingId: _id },
        }

        await externalPromocodeStore.validateExternalPromocode_api(promocodeData)
      }

      const response = await requester.acceptExternalBooking(this.saveArgs())
      this.setLoading(false)

      if (!response.acceptExternalBooking) {
        return
      }

      this.mergeFromServer(response.acceptExternalBooking)

      return response.acceptExternalBooking
    } catch (e) {
      this.setLoading(false)
      return
    }
  }

  completeExternalBooking_api = async (
    paymentType: CompletePaymentMethodOptionId,
    finalPaymentAmount = this.balanceOutstanding,
  ) => {
    const { communication: { requester } } = this.rootStore
    const { _id } = this.data

    if (!_id) return
    this.setLoading(true)

    const data: CompleteExternalBookingInput = { paymentType, finalPaymentAmount }

    try {
      const response = await requester.completeExternalBooking({ _id, data })
      this.setLoading(false)

      if (!response.completeExternalBooking) {
        return
      }

      this.mergeFromServer(response.completeExternalBooking)

      return response.completeExternalBooking._id
    } catch (e) {
      this.setLoading(false)
      return
    }
  }

  acceptRescheduledExternalBooking_api = async () => {
    const { communication: { requester } } = this.rootStore
    const { _id } = this.data

    if (!_id) return
    this.setLoading(true)

    try {
      const response = await requester.acceptRescheduledBooking({ originalBookingId: _id })
      this.setLoading(false)

      if (!response.acceptRescheduledBooking) {
        return
      }

      this.data.rescheduleBookingId = undefined
      response.acceptRescheduledBooking.rescheduleBookingId = undefined

      this.mergeFromServer(response.acceptRescheduledBooking)

      return response.acceptRescheduledBooking
    } catch (e) {
      this.setLoading(false)
      return
    }
  }

  rejectRescheduledExternalBooking_api = async () => {
    const { communication: { requester } } = this.rootStore
    const { _id } = this.data

    if (!_id) return
    this.setLoading(true)

    try {
      const response = await requester.rejectRescheduledBooking({ originalBookingId: _id })
      this.setLoading(false)

      if (!response.rejectRescheduledBooking) {
        return
      }

      this.data.rescheduleBookingId = undefined
      response.rejectRescheduledBooking.rescheduleBookingId = undefined

      this.mergeFromServer(response.rejectRescheduledBooking)

      return response.rejectRescheduledBooking
    } catch (e) {
      this.setLoading(false)
      return
    }
  }

  declineExternalBooking_api = async (reason: string, deleteClient: boolean) => {
    const { communication: { requester } } = this.rootStore
    const { _id } = this.data

    if (!_id) return
    this.setLoading(true)

    try {
      const response = await requester.declineExternalBooking({ _id, reason, deleteClient })
      this.setLoading(false)

      if (!response.declineExternalBooking) {
        return
      }

      this.mergeFromServer(response.declineExternalBooking)

      return
    } catch (e) {
      this.setLoading(false)
      return
    }
  }

  cancelAndChargeExternalBooking_api = async (amount: number, cancellationReason: CancellationReason = CancellationReason.Cancelled) => {
    const { communication: { requester } } = this.rootStore
    const { _id } = this.data

    const failResult = { cancelled: false, charged: false, error: '' }

    if (!_id) return failResult
    this.setLoading(true)

    try {
      const response = await requester.cancelAndChargeExternalBooking({ data: { _id, amount, cancellationReason } })
      this.setLoading(false)

      if (!response.cancelAndChargeExternalBooking) {
        return failResult
      }
      const { externalBooking, charged, error } = response.cancelAndChargeExternalBooking

      this.mergeFromServer(externalBooking)

      return { cancelled: true, charged, error }
    } catch (e) {
      this.setLoading(false)
      return failResult
    }
  }

  cancelExternalBooking_api = async (refund: number, cancellationReason: CancellationReason = CancellationReason.Cancelled, giftCardRefund: number) => {
    const { communication: { requester } } = this.rootStore
    const { _id } = this.data

    if (!_id) return
    this.setLoading(true)

    try {
      const response = await requester.cancelAndRefundExternalBooking({
        data: {
          _id,
          refund,
          cancellationReason,
          giftCardRefund,
        },
      })
      this.setLoading(false)

      if (!response.cancelAndRefundExternalBooking) {
        return false
      }

      this.mergeFromServer(response.cancelAndRefundExternalBooking)

      return true
    } catch (e) {
      this.setLoading(false)
      return false
    }
  }

  chargeExternalBooking_api = async (data: ChargeExternalBookingInput) => {
    const { communication: { requester }, i18n: { s }, dropdownMessageHandler } = this.rootStore
    const { _id } = this.data

    if (!_id) return
    this.setLoading(true)

    try {
      const response = await requester.chargeExternalBooking({ data })
      this.setLoading(false)

      if (!response.chargeExternalBooking) {
        return false
      }

      const { externalBooking, charged, error } = response.chargeExternalBooking

      this.mergeFromServer(externalBooking)

      if (charged) {
        dropdownMessageHandler.showDropdownMessage({
          title: 'The fee has been charged to the clients card.',
          type: 'success',
          closeInterval: 1000,
        })
      } else {
        dropdownMessageHandler.showDropdownMessage({
          title: s('There was an issue charging the fee to the clients card. $0', error),
          type: 'error',
          closeInterval: 1000,
        })
      }

      return charged
    } catch (e) {
      this.setLoading(false)
      return false
    }
  }

  updateSpecificExternalBookingData_api = async (data: ExternalBookingSpecificInput) => {
    const { communication: { requester } } = this.rootStore
    const { _id } = this.data

    if (!_id) return
    this.setLoading(true)

    try {
      const response = await requester.updateSpecificExternalBookingData({ data })
      this.setLoading(false)

      if (!response.updateSpecificExternalBookingData) {
        return false
      }

      this.mergeFromServer(response.updateSpecificExternalBookingData)

      return true
    } catch (e) {
      this.setLoading(false)
      return false
    }
  }

  sendReminder_api = async (reminder: 'consultationForm' | 'deposit'): Promise<{ sent: boolean, failureMessage?: string }> => {
    const { communication: { requester } } = this.rootStore
    const { _id } = this.data

    if (!_id) return { sent: false }
    this.setLoading(true)

    try {
      const response = await requester.sendReminder({ _id, reminder })
      this.setLoading(false)

      if (!response.sendReminder) {
        throw new Error('Failed to send Reminder')
      }

      this.mergeFromServer(response.sendReminder)

      return { sent: true }
    } catch (e) {
      this.setLoading(false)

      return requestErrorCode(e) === BOOKING_REMINDER_NOT_SENT
        ? { sent: false, failureMessage: (e as Error).message }
        : { sent: false }
    }
  }

  @action setExternalClient = (externalClient: ExternalClient) => {
    this.data.externalClientId = externalClient._id
    this.data.externalClient = { ...externalClient }
    this.data.color = externalClient.color
    this.data.emailUpdatesEnabled = !!(externalClient.emailAddress || externalClient.mobile)
  }

  removeExternalClient = () => {
    this.data.externalClientId = undefined
    this.data.externalClient = undefined
    this.data.address = undefined
  }

  setAddressForEvent = (address: $ExternalClientAddress) => {
    this.data.address = address
    if (address.timezone) {
      this.data.timezone = address.timezone
    }
  }

  addItems = (items: Array<$ExternalBookingItem>) => {
    this.data.items = [...this.data.items, ...items]
  }

  addPromocode = (promocode: ExternalPromocode) => {
    this.data.promocodeId = promocode._id
    this.data.promocodeDiscount = {
      code: promocode.code,
      amount: promocode.discountAmount,
      type: promocode.discountType,
    }
  }

  removePromocode = () => {
    this.data.promocodeId = ''
    this.data.promocodeDiscount = {
      code: '',
      amount: 0,
      type: '',
    }
  }

  removeItems = (itemIds: Array<string>) => {
    this.data.items = this.data.items.filter(i => !itemIds.includes(i._id))
  }

  // Disguise our legacy fee as a new-format fee item at fetch - merge. Keep the name unique so we can recognise it later via isLegacyBookingFee.
  @action normaliseLegacyServiceFee = () => {
    if (this.serviceFeeItem) return // already has a canonical fee item — nothing to do

    const legacyItem = isLegacyDepositBooking(this.data)
    if (!legacyItem) return

    // The fee was charged as a % of the deposit the customer paid up front.
    const depositPayment = this.data.payments.find(p => p.description === 'Deposit')
    const depositBase = (depositPayment?.amount ?? 0) - legacyItem.fee
    if (depositBase <= 0) return

    legacyItem.category = SERVICE_FEE_ITEM_NAME
    legacyItem.name = LEGACY_SERVICE_FEE_ITEM_NAME
    this.data.serviceFeePercentage = (legacyItem.fee / depositBase) * 100
  }

  // Button "Update booking fee" action handler. Update legacy fee to the current rate.
  updateLegacyServiceFee = (itemIds: Array<string>) => {
    const soloServiceFee = this.rootStore.userStore.data.soloServiceFee
    this.data.serviceFeePercentage = normaliseServiceFeePercentage(soloServiceFee)

    const calculatedFee = getServiceFee({
      lineItems: this.billableItems,
      serviceFee: this.data.serviceFeePercentage,
      discount: this.discount,
    })

    this.data.items = this.data.items.map(item => {
      if (!itemIds.includes(item._id)) return item
      return {
        ...item,
        category: SERVICE_FEE_ITEM_NAME,
        name: this.rootStore.i18n.s('BOOKING_FEE'),
        fee: calculatedFee,
      }
    })
  }

  updateItem = (item: $ExternalBookingItem) => {
    const existingItem = this.data.items.find(i => i._id === item._id)

    if (existingItem) {
      Object.keys(item).forEach(key => {
        existingItem[key as keyof typeof existingItem] = item[key as keyof typeof existingItem] as never
      })
    }
  }

  resolveRescheduledBooking_api = async (date: string) => {
    const { communication: { requester } } = this.rootStore
    if (!this.data._id || !this.data.date) return

    this.loading = true

    try {
      const response = await requester.resolveRescheduledBooking({ originalBookingId: this.data._id, date })
      this.loading = false

      if (!response.resolveRescheduledBooking) {
        return undefined
      }

      this.data.rescheduleBookingId = undefined
      this.mergeFromServer(response.resolveRescheduledBooking)

      return this
    } catch (e) {
      this.loading = false
      return this
    }
  }

  saveArgs = (): { data: ExternalBookingInput, unsetFields?: string[] } => {
    const data = {
      ...this.data as any,
      source: undefined,
      externalClient: undefined,
      externalReview: undefined,
    }

    return {
      data,
      ...(this.pendingUnsetFields ? { unsetFields: this.pendingUnsetFields } : {}),
    }
  }

  get listStore() {
    return this.rootStore.externalBookingStore
  }

  @computed get promocodeDetails() {
    return this.rootStore.externalPromocodeStore.findItemById(this.data.promocodeId)?.data
  }

  @computed get billableItems() {
    return this.data.items.filter(item => item.category !== SERVICE_FEE_ITEM_NAME)
  }

  @computed get serviceFeeItem(): $ExternalBookingItem | null {
    return this.data.items.find(item => item.category === SERVICE_FEE_ITEM_NAME) ?? null
  }

  // Treatments and custom items only: the booking fee and travel surcharge are charges on top of
  // the work, so a booking holding just those has nothing booked.
  @computed get treatmentItems() {
    return this.data.items.filter(item => item.category !== SERVICE_FEE_ITEM_NAME && item.category !== TRAVEL_FEE_CATEGORY)
  }

  @computed get travelFeeItem(): $ExternalBookingItem | null {
    return this.data.items.find(item => item.category === TRAVEL_FEE_CATEGORY) ?? null
  }

  @computed get totalBeforeDiscount() {
    return this.billableItems.reduce((n: number, item: $ExternalBookingItem) => n + item.fee, 0)
  }

  @computed get discount() {
    if (!this.hasPromocode) return 0

    if (this.data.promocodeDiscount?.type === 'monies') {
      return Math.min(this.data.promocodeDiscount.amount, this.totalBeforeDiscount)
    }

    if (this.data.promocodeDiscount?.type === 'percent') {
      const discountedTotal = calculateTotalForPercentage(
        this.billableItems,
        !this.promocodeDetails || !!this.promocodeDetails.applyToAllTreatments,
        (this.promocodeDetails?.treatmentIds ?? []),
        this.data.promocodeDiscount.amount,
      )
      return this.totalBeforeDiscount - discountedTotal
    }

    return 0
  }

  @computed get serviceFeeAmount() {
    // serviceFeePercentage is the single source of truth: the % the booking was created under.
    // No % means no service fee (getServiceFee returns 0). We never read the pro's current %.
    return getServiceFee({
      lineItems: this.billableItems,
      serviceFee: this.data.serviceFeePercentage,
      discount: this.discount,
    })
  }

  // Simple check against the name that we set on merge for Legacy Bookings with a paid deposit
  @computed get isLegacyBookingFee() {
    return this.serviceFeeItem?.name === LEGACY_SERVICE_FEE_ITEM_NAME
  }

  @computed get serviceFeePercentage() {
    return this.data.serviceFeePercentage
  }

  @computed get computedTotalFee() {
    // The service fee only counts toward the total when a fee item is actually on the
    // booking. serviceFeeAmount is derived from the pro's soloServiceFee % and is used to
    // keep that item's fee in sync — it must NOT be re-added here, otherwise removing the
    // fee item would leave the total (and balanceOutstanding) unchanged.
    const serviceFee = this.serviceFeeItem?.fee ?? 0
    return Math.max(0, this.totalBeforeDiscount - this.discount) + serviceFee
  }

  @computed get computedTotalDuration() {
    const { items, type, totalDuration, bufferTime } = this.data
    if (type === 'event' || type === 'reschedule') return totalDuration
    return items.reduce((n, item) => n + item.duration, 0) + bufferTime
  }

  @computed get dateInTimezone() {
    return dateInTimezone(this.dateAsDate, this.data.timezone)
  }

  @computed get dateAsDate() {
    return new Date(this.data.date)
  }

  @computed get pastEndDate() {
    return momentTz(this.dateInTimezone).add(this.computedTotalDuration, 'minutes').isBefore(momentTz())
  }
  @computed get closeToStartDate() {
    return momentTz(this.dateInTimezone).isBefore(momentTz().add(1, 'day'))
  }

  @computed get formattedDateForList(): string {
    return this.dateInTimezone.format('Do MMMM YYYY')
  }

  @computed get formattedDateShort(): string {
    return this.dateInTimezone.format('ddd Do MMM YYYY')
  }

  @computed get hasPromocode() {
    return !!this.data.promocodeId
  }

  @computed get ownerId() {
    return this.data.professionalId || this.rootStore.userStore.data._id
  }

  // Format date + booking duration
  @computed get formattedDateTimeBrackets(): string {
    const { timezone } = this.rootStore.userStore
    const duration = this.data.type === 'event' ? this.data.totalDuration : this.computedTotalDuration
    const brackets = timeBrackets(this.dateInTimezone, duration)

    const timeZone = momentTz.tz.zone(this.data.timezone ?? timezone)
    if (!!timeZone && this.data.date) {
      return `${brackets} ${timeZone.abbr(new Date(this.data.date).getTime())}`
    }

    return brackets
  }

  @computed get activeTimeBrackets(): string[] | null {
    const { items, type } = this.data
    if (type === 'event') return null

    const hasProcessing = items.some(item =>
      item.schedule?.some(entry => entry.type === ScheduleEntryType.Processing),
    )
    if (!hasProcessing) return null

    const { timezone } = this.rootStore.userStore
    const timeZone = momentTz.tz.zone(this.data.timezone ?? timezone)
    const tzAbbr = timeZone && this.data.date
      ? ` ${timeZone.abbr(new Date(this.data.date).getTime())}`
      : ''

    const brackets: string[] = []
    let cursor = momentTz(this.dateInTimezone)
    let activeStart = momentTz(cursor)

    for (const item of items) {
      const schedule = item.schedule?.filter(e => e.duration > 0)
      const itemHasProcessing = schedule?.some(e => e.type === ScheduleEntryType.Processing)

      if (!schedule?.length || !itemHasProcessing) {
        cursor = momentTz(cursor).add(item.duration, 'minutes')
        continue
      }

      for (const entry of schedule) {
        if (entry.type === ScheduleEntryType.Processing) {
          brackets.push(`${activeStart.format('H:mm')} - ${cursor.format('H:mm')}${tzAbbr}`)
          cursor = momentTz(cursor).add(entry.duration, 'minutes')
          activeStart = momentTz(cursor)
        } else {
          cursor = momentTz(cursor).add(entry.duration, 'minutes')
        }
      }
    }

    if (cursor.isAfter(activeStart)) {
      brackets.push(`${activeStart.format('H:mm')} - ${cursor.format('H:mm')}${tzAbbr}`)
    }

    return brackets.length > 1 ? brackets : null
  }

  @computed get balanceOutstanding() {
    return Math.ceil((this.computedTotalFee ?? 0) - this.amountPaid)
  }

  outstandingAmount(depositOnly: boolean) {
    const outstanding = depositOnly ? this.depositOutstanding : this.balanceOutstanding
    return outstanding > 0 ? outstanding : undefined
  }

  @computed get originalBookingPrice() {
    const { feeAtBookingTime } = this.data
    return feeAtBookingTime && feeAtBookingTime > 0 ? feeAtBookingTime : this.computedTotalFee
  }

  @computed get maxChargeableAmount() {
    const chargeCeiling = Math.max(
      this.originalBookingPrice * MAX_CHARGE_MULTIPLE_OF_ORIGINAL_PRICE,
      this.computedTotalFee,
    )
    return Math.max(0, Math.ceil(chargeCeiling - this.amountPaid))
  }

  @computed get tips() {
    return Math.max(0, -this.balanceOutstanding)
  }

  @computed get amountPaid() {
    return this.data.payments.reduce(
      (n: number, payment: StripePaymentDetails) => n + payment.amount,
      0,
    )
  }

  @computed get paymentLinks() {
    return this.data.payments.filter(payment => payment.type === PaymentTypes.Stripe)
  }

  @computed get paymentLinksBalance() {
    return this.paymentLinksAmount + this.paymentLinksRefundAmount
  }

  @computed get paymentLinksAmount() {
    if (this.wasRequestedWithDeposit) return 0
    return this.data.payments.filter(payment => [PaymentTypes.Stripe].includes((payment.type ?? '') as PaymentTypes))
      .reduce(
        (n: number, payment: StripePaymentDetails) => n + payment.amount,
        0,
      )
  }

  @computed get giftCardPaymentsAmount() {
    return this.data.payments.filter(payment => [PaymentTypes.GiftCard].includes((payment.type ?? '') as PaymentTypes))
      .reduce(
        (n: number, payment: StripePaymentDetails) => n + payment.amount,
        0,
      )
  }

  @computed get giftCardRefundPaymentsAmount() {
    return this.data.payments.filter(payment => [PaymentTypes.GiftCardRefund].includes((payment.type ?? '') as PaymentTypes))
      .reduce(
        (n: number, payment: StripePaymentDetails) => n + payment.amount,
        0,
      )
  }

  @computed get giftCardBalance() {
    return this.giftCardPaymentsAmount + this.giftCardRefundPaymentsAmount
  }

  @computed get paymentLinksRefundAmount() {
    return this.data.payments.filter(payment => [PaymentTypes.StripeRefund].includes((payment.type ?? '') as PaymentTypes))
      .reduce(
        (n: number, payment: StripePaymentDetails) => n + payment.amount,
        0,
      )
  }

  @computed get otherPaymentsAmount() {
    return this.data.payments.filter(payment => [PaymentTypes.Bank, PaymentTypes.Cash, PaymentTypes.Machine, PaymentTypes.Other].includes((payment.type ?? '') as PaymentTypes))
      .reduce(
        (n: number, payment: StripePaymentDetails) => n + payment.amount,
        0,
      )
  }

  @computed get canCancel() {
    return [
      ExternalBookingStatus.Accepted,
      ExternalBookingStatus.CancelRequested,
      ExternalBookingStatus.ToComplete,
    ].includes(this.data.status)
  }

  @computed get canRebook() {
    return [
      ExternalBookingStatus.Accepted,
      ExternalBookingStatus.ToComplete,
      ExternalBookingStatus.Completed,
    ].includes(this.data.status)
  }

  @computed get canSetDepositDetails() {
    return [
      ExternalBookingStatus.Requested,
    ].includes(this.data.status)
      || !this.data._id // Creating a new booking
  }

  @computed get canAddPayments() {
    return bookingAcceptsPayments(this.data.status)
  }

  @computed get depositPaid() {
    const { depositPercentage, paymentMethod } = this.data

    if (depositPercentage === 0) return false

    if (paymentMethod === PaymentMethodOptionId.PaymentLink) {
      if (!this.stripeDepositLink && this.amountPaid >= this.depositAmount) return true
      return !!this.stripeDepositLink
    } else if (this.amountPaid >= this.depositAmount) {
      return true
    }

    return false
  }

  @computed get depositAmount() {
    if (this.data.useFixedAmount) {
      return this.data.depositPercentage <= this.computedTotalFee
        ? this.data.depositPercentage
        : this.computedTotalFee
    }
    return this.computedTotalFee * this.data.depositPercentage / 100
  }

  @computed get depositOutstanding() {
    const { depositPercentage, depositRequired } = this.data
    if (!depositPercentage) return 0
    if (depositRequired === UpfrontPaymentOption.None) return 0
    return Math.max(0, Math.ceil(this.depositAmount - this.amountPaid))
  }

  @computed get collectDepositFirst() {
    return this.depositOutstanding > 0 && !this.closeToStartDate
  }

  suggestedPaymentAmount = (amountDue: number) => {
    const due = Math.max(0, amountDue)
    return this.collectDepositFirst ? Math.min(this.depositOutstanding, due) : due
  }

  @computed get stripeDepositLink() {
    const { payments } = this.data
    return payments.find(p => p.description === 'Deposit' && p.type === PaymentTypes.Stripe)
  }

  @computed get _unsafeTypeData() {
    return this.data as $ExternalBookingMutable
  }

  @computed get showPaymentStatus() {
    return ![ExternalBookingStatus.Requested, ExternalBookingStatus.Cancelled].includes(this.data.status) && this.computedTotalFee > 0
  }

  @computed get showConsultationStatus() {
    return ![ExternalBookingStatus.Requested, ExternalBookingStatus.Cancelled, ExternalBookingStatus.CancelRequested].includes(this.data.status)
      && this.data.consultationFormStatus !== ConsultationFormStatus.NotRequired
      && !this.pastEndDate
  }

  @action setLoading = (loading: boolean) => {
    this.loading = loading
  }

  @computed get wasRequestedWithDeposit() {
    const { source, payments, depositRequired, status } = this.data
    return source !== 'PRO_APP'
      && payments.length === 1
      && depositRequired === UpfrontPaymentOption.Checkout
      && status === ExternalBookingStatus.CancelRequested
      && payments[0].type === PaymentTypes.Stripe
  }

  showConflictingBookingWarning = async (saveCallBack: () => void) => {
    const {
      uiStore: { showMessage },
      i18n: { s },
    } = this.rootStore

    showMessage({
      title: s('Warning'),
      body: s('This booking conflicts with an existing booking or event in your calendar. Are you sure you want to continue?'),
      buttons: [
        {
          title: s('Continue & save'), onPress: saveCallBack,
        },
        { title: s('Cancel') },
      ],
    })
  }
}
